// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner on Quest, 2026-11-04: "The answer is just typed not read to the
 * user"): the AYNI chatbot's replies read aloud on this site, through one
 * audio output unlocked by the press that starts the turn.
 *
 * AYNI 3D's `SpeechPlayer` (copied unchanged in `src/ayni3d/`) opens a new
 * `<audio>` for every sentence. A browser that lets sound play only just after
 * a press refuses those (`NotAllowedError`, swallowed, so the reply stayed on
 * screen in silence): Chromium on Android and on the Quest is one, and a
 * reply comes seconds after the mic was pressed. So the press itself
 * (`unlock`, from the mic's own handler: the dock's or the panel's) starts a
 * Web Audio context, and every sentence plays through it. The rest is AYNI
 * 3D's way: a sentence at a time, the next fetched while one plays; when the
 * site's voice cannot read it, the device's own voice reads on (`DeviceVoice`,
 * primed by the same press); with neither, `onError` (the headset says the
 * reply was not read aloud). Without Web Audio (or with it refused), a clip
 * plays through an `<audio>` as before.
 *
 * AYNI (owner on Quest, 2026-11-05: still "no text to voice, it only replied
 * by text"): a press on a controller may not count as a press to the page,
 * so the output the mic's press opened can stay shut; the `<audio>` it fell
 * back on was refused in silence and nothing said so. Now the output is also
 * opened by the click that enters the headset (`unlock` from
 * `VrSessionContext.primeAudio`, a real click), with a moment of silence
 * played in it; a clip waits briefly for the output to open; a clip the
 * browser refuses hands the reply to the device's voice; and whatever stops
 * the reading is said, with its reason (`UnreadReason`).
 */

import { DeviceVoice, audioMimeType, type SpeakPiece } from '../ayni3d/renderer/ui/ai-speech-io'
import { splitForSpeech } from '../ayni3d/shared/ai-speakable-text'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'

/** Failures after which the device's own voice reads instead (AYNI 3D's list). */
const FALL_BACK_TO_DEVICE = new Set(['AI_UNAVAILABLE', 'AI_NOT_CONFIGURED', 'AI_INVALID_REQUEST', 'AI_DISABLED', 'AI_QUOTA', 'AUDIO_BLOCKED'])

/** The code for a clip the browser would not play (no press it counted). */
export const AUDIO_BLOCKED = 'AUDIO_BLOCKED'
/** The code for a language no voice here reads. */
export const NO_VOICE = 'NO_VOICE'
/** How long a clip waits for the output to open. */
const RESUME_WAIT_MS = 600

/** Why a reply was not read aloud, as the headset says it. */
export type UnreadReason = 'blocked' | 'quota' | 'server' | 'no-voice'

export function unreadReason(code: string): UnreadReason {
  if (code === AUDIO_BLOCKED) return 'blocked'
  if (code === 'AI_QUOTA') return 'quota'
  if (code === NO_VOICE) return 'no-voice'
  return 'server'
}

type AudioContextLike = Pick<AudioContext, 'state' | 'resume' | 'decodeAudioData' | 'createBufferSource' | 'destination'> &
  Partial<Pick<AudioContext, 'createBuffer'>>

/** Waits for `work`, but no longer than `ms`. */
async function settle(work: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([work.catch(() => {}), new Promise<void>((resolve) => { timer = setTimeout(resolve, ms) })])
  clearTimeout(timer)
}

export class UnlockedSpeech {
  private generation = 0
  private context: AudioContextLike | null = null
  private source: AudioBufferSourceNode | null = null
  private element: HTMLAudioElement | null = null
  private readonly deviceLanguages = new Set<AiChatLanguage>()

  constructor(
    private readonly speakPiece: SpeakPiece | null,
    private readonly onError: (code: string) => void,
    private readonly device: DeviceVoice | null = null,
    private readonly makeContext: () => AudioContextLike | null = () => (typeof AudioContext === 'undefined' ? null : new AudioContext()),
  ) {}

  /**
   * Call from a press (a user activation): the click that enters the
   * headset, and the press that starts a turn. Opens the audio output that
   * every later sentence plays through, and primes the device's voice.
   */
  public unlock(): void {
    try {
      this.context ??= this.makeContext()
      const context = this.context
      if (context) {
        if (context.state !== 'running') void context.resume().catch(() => {})
        // A moment of silence, started inside the press: some browsers open the output only once something plays.
        if (typeof context.createBuffer === 'function') {
          const hush = context.createBufferSource()
          hush.buffer = context.createBuffer(1, 1, 22_050)
          hush.connect(context.destination)
          hush.start()
        }
      }
    } catch {
      this.context = null
    }
    try {
      if (this.device && typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined') {
        // A silent word, said inside the press, lets the device speak later.
        const hush = new SpeechSynthesisUtterance(' ')
        hush.volume = 0
        window.speechSynthesis.speak(hush)
      }
    } catch { /* no device voice to prime */ }
  }

  /** Whether any voice can read this language. */
  public canSpeak(language: AiChatLanguage): boolean {
    return Boolean(this.speakPiece) || Boolean(this.device?.has(language))
  }

  /** Reads the reply; resolves once it has been read, cut short or failed. */
  public async say(text: string, language: AiChatLanguage): Promise<void> {
    this.stop()
    const generation = ++this.generation
    const pieces = splitForSpeech(text, 200)
    if (!pieces.length) return
    if (!this.speakPiece || this.deviceLanguages.has(language)) {
      await this.sayOnDevice(pieces, language, generation, NO_VOICE)
      return
    }
    let next: ReturnType<SpeakPiece> | null = this.speakPiece(pieces[0]!, language)
    for (let index = 0; index < pieces.length && next; index++) {
      const result = await next
      if (generation !== this.generation) return
      if (!result.ok) {
        await this.handOver(pieces.slice(index), language, generation, result.code)
        return
      }
      next = index + 1 < pieces.length ? this.speakPiece(pieces[index + 1]!, language) : null
      const played = await this.play(result.audio, generation)
      if (generation !== this.generation) return
      if (!played) {
        // The browser kept the sound off: the device's voice reads on, or the headset says why.
        await this.handOver(pieces.slice(index), language, generation, AUDIO_BLOCKED)
        return
      }
    }
  }

  /** The site's voice stopped (`code`): the device's reads the rest when it can, else the reason is told. */
  private async handOver(pieces: string[], language: AiChatLanguage, generation: number, code: string): Promise<void> {
    if (FALL_BACK_TO_DEVICE.has(code) && this.device?.has(language)) {
      // A refusal by the server holds for the session; a refused clip only for this reply.
      if (code !== AUDIO_BLOCKED) this.deviceLanguages.add(language)
      await this.sayOnDevice(pieces, language, generation, code)
      return
    }
    this.onError(code)
  }

  public stop(): void {
    this.generation++
    this.device?.stop()
    if (this.source) {
      try { this.source.stop() } catch { /* already over */ }
      this.source = null
    }
    if (this.element) {
      this.element.pause()
      URL.revokeObjectURL(this.element.src)
      this.element = null
    }
  }

  private async sayOnDevice(pieces: string[], language: AiChatLanguage, generation: number, why: string): Promise<void> {
    if (!this.device?.has(language)) {
      this.onError(why)
      return
    }
    for (const piece of pieces) {
      if (generation !== this.generation) return
      await this.device.say(piece, language)
    }
  }

  /** One clip: through the unlocked output when it runs, else an `<audio>`; false when the browser refused it. */
  private async play(clip: Uint8Array, generation: number): Promise<boolean> {
    const context = this.context
    if (context && context.state !== 'running') await settle(context.resume(), RESUME_WAIT_MS)
    if (context && context.state === 'running') {
      try {
        const buffer = await context.decodeAudioData(clip.slice().buffer as ArrayBuffer)
        if (generation !== this.generation) return true
        await new Promise<void>((resolve) => {
          const source = context.createBufferSource()
          source.buffer = buffer
          source.connect(context.destination)
          this.source = source
          source.onended = () => {
            if (this.source === source) this.source = null
            resolve()
          }
          source.start()
        })
        return true
      } catch {
        // A clip it cannot decode plays the old way.
      }
    }
    return this.playElement(clip, generation)
  }

  private playElement(clip: Uint8Array, generation: number): Promise<boolean> {
    return new Promise((resolve) => {
      if (generation !== this.generation) {
        resolve(true)
        return
      }
      const audio = new Audio(URL.createObjectURL(new Blob([new Uint8Array(clip)], { type: audioMimeType(clip) })))
      this.element = audio
      const done = (played: boolean) => {
        URL.revokeObjectURL(audio.src)
        if (this.element === audio) this.element = null
        resolve(played)
      }
      audio.onended = () => done(true)
      audio.onerror = () => done(true)
      // Refused for want of a press: told, not swallowed.
      audio.play().catch((err: unknown) => done((err as { name?: unknown } | null)?.name !== 'NotAllowedError'))
    })
  }
}
